import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Home } from "lucide-react";

/** Jul-2026 (User-Wunsch): Mitarbeiter werden nach dem Login aufgefordert, ihre
 *  aktuelle Wohnadresse zu erfassen (für den regionalen Vertretungsplan) —
 *  inkl. DSGVO-Zustimmung. Erinnerung bei jedem Login, bis erfasst. */
export const HomeAddressPrompt = () => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ street: "", zip: "", city: "" });
    const [consent, setConsent] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (sessionStorage.getItem("tw_home_addr_later")) return;
        api.get("/me/home-address").then(({ data }) => {
            if (!(data?.applicable && !data.set)) return;
            // Aug-2026 (Bugfix): nicht über einem bereits offenen Dialog stapeln —
            // warten, bis kein anderer Dialog (z. B. Verkaufsprozess) aktiv ist.
            const tryOpen = (attempt = 0) => {
                if (sessionStorage.getItem("tw_home_addr_later")) return;
                if (!document.querySelector('[role="dialog"]')) { setOpen(true); return; }
                if (attempt < 40) setTimeout(() => tryOpen(attempt + 1), 3000);
            };
            tryOpen();
        }).catch(() => {});
    }, []);

    const save = async () => {
        if (!form.zip.trim() || !form.city.trim()) { toast.error("Bitte mindestens PLZ und Ort angeben."); return; }
        if (!consent) { toast.error("Bitte der Datenschutzerklärung zustimmen."); return; }
        setBusy(true);
        try {
            await api.put("/me/home-address", { ...form, consent: true });
            toast.success("Wohnadresse gespeichert — danke!");
            setOpen(false);
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        setBusy(false);
    };
    const later = () => {
        sessionStorage.setItem("tw_home_addr_later", "1");
        setOpen(false);
        toast("Alles klar — ich erinnere dich beim nächsten Login wieder.");
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) later(); }}>
            <DialogContent className="max-w-md" data-testid="home-address-prompt">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Home className="w-5 h-5 text-[#FFCC00]" /> Wohnadresse erfassen</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-gray-600">
                    Für den standortübergreifenden <strong>Vertretungsplan</strong> benötigen wir deine aktuelle
                    Wohnadresse (Berechnung der Anfahrt zu den Filialen). Bitte einmalig erfassen.
                </p>
                <div className="space-y-2">
                    <Input placeholder="Straße & Hausnummer" value={form.street}
                        onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))} data-testid="home-addr-street" />
                    <div className="grid grid-cols-3 gap-2">
                        <Input placeholder="PLZ" value={form.zip}
                            onChange={(e) => setForm((f) => ({ ...f, zip: e.target.value }))} data-testid="home-addr-zip" />
                        <Input placeholder="Ort" className="col-span-2" value={form.city}
                            onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} data-testid="home-addr-city" />
                    </div>
                </div>
                <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                    <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="home-addr-consent" />
                    <span>
                        Ich stimme zu, dass meine Wohnadresse zur <strong>Vertretungs- und Einsatzplanung</strong> verarbeitet
                        wird (Anfahrtsberechnung zu Filialen). Die Zustimmung ist freiwillig und jederzeit widerruflich.
                        Details in der <a href="/datenschutz" target="_blank" rel="noreferrer" className="underline font-semibold">Datenschutzerklärung</a>.
                    </span>
                </label>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={later} className="rounded-sm" data-testid="home-addr-later">Später</Button>
                    <Button onClick={save} disabled={busy} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm" data-testid="home-addr-save">
                        {busy ? "Speichere…" : "Speichern"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default HomeAddressPrompt;
